import { ChangeDetectionStrategy, Component, input, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { ColorSchemeService } from '@examdojo/browser/color-scheme';
import { PageType, PageVersionQuery } from '@examdojo/category/v2';
import { DateTimeModule } from '@examdojo/core/date-time';
import { EmptyStateComponent } from '@examdojo/core/empty-state';
import { TiptapEditorService } from '@examdojo/core/tiptap';
import { TiptapEditorWrapperComponent } from '@examdojo/core/tiptap-react-ui-components';
import { distinctUntilArrayChanged } from '@examdojo/rxjs';
import { TranslocoPipe } from '@jsverse/transloco';
import { filter, map, merge, startWith, switchMap, tap } from 'rxjs';
import { RootUrlParts } from '../../../../../app.model';
import { CardComponent } from '../../../card-link/card-link.component';

@Component({
  selector: 'dojo-topic-concept-tab',
  imports: [
    DateTimeModule,
    EmptyStateComponent,
    TranslocoPipe,
    CardComponent,
    RouterLink,
    TiptapEditorWrapperComponent,
  ],
  templateUrl: './topic-tab.component.html',
  styleUrl: './topic-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [],
  animations: [],
})
export class TopicTabComponent {
  constructor(
    private readonly pageVersionQuery: PageVersionQuery,
    private readonly tiptapEditorService: TiptapEditorService,
    private readonly colorSchemeService: ColorSchemeService,
  ) {
    merge(
      this.activePageVersion$.pipe(
        map((page) => page?.content),
        tap((content) => {
          this.contentCtrl.setValue(content ?? '');
        }),
      ),
      toObservable(this.notionEditorWrapper).pipe(
        filter(Boolean),
        switchMap((wrapper) =>
          merge(
            wrapper.editor$.pipe(
              filter(Boolean),
              tap((editor) => this.tiptapEditorService.registerEditor(editor)),
            ),
            wrapper.tableOfContentData$.pipe(
              filter(Boolean),
              distinctUntilArrayChanged(),
              tap((data) => this.tiptapEditorService.registerTableOfContentData(data)),
            ),
          ),
        ),
        takeUntilDestroyed(),
      ),
    )
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  readonly notionEditorWrapper = viewChild(TiptapEditorWrapperComponent);

  readonly pageType = input.required<PageType>();

  readonly activePageVersion$ = toObservable(this.pageType).pipe(
    switchMap((pageType) => this.pageVersionQuery.getPublishedVersionForType(pageType)),
    startWith(undefined),
  );

  readonly contentCtrl = new FormControl<string>(``, { nonNullable: true });

  readonly state = connectState({
    activePageVersion: this.activePageVersion$,
    scrollableContainer: this.tiptapEditorService.scrollingContainer$,
    darkMode: this.colorSchemeService.hasDarkMode$,
  });

  readonly RootUrlParts = RootUrlParts;
}
